import React, { useEffect, useRef, useState } from "react";
import { parseFloatData } from "utils/UtilityFuncs";

export default function StaticAutoComplete({
  required,
  type,
  resultsKey,
  is2digitFloat,
  fieldName,
  placeholder,
  handleChange,
  classNames,
  suggestionsWidth,
  styles = {},
  value,
  dataList, // Predefined list of data objects
  searchKeys = [], // Array of keys to search within each object (defaults to just fieldName if empty)
}) {
  /*   All States
   ********************************************* */
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  const autoCompleteRef = useRef();

  /*   All Functions
   ********************************************* */
  const getAllResults = () => {
    if (showSuggestions) {
      setShowSuggestions(false);
      return;
    }
    setSearchResults(dataList);
    setShowSuggestions(true);
  };

  const filterDataList = (searchText) => {
    /**
     * Filters the predefined dataList based on search text
     */
    if (!searchText) return dataList;

    const keysToSearch = searchKeys.length ? searchKeys : [fieldName];
    const lowercasedSearchText = searchText.toLowerCase();

    const results = dataList.filter((item) => {
      return keysToSearch.some((key) => {
        const value = item[key];
        return (
          value && value.toString().toLowerCase().includes(lowercasedSearchText)
        );
      });
    });

    // Remove duplicates based on fieldName
    const uniqueResults = results.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t[fieldName] === item[fieldName])
    );

    return uniqueResults;
  };

  /*   All UseEffects
   ********************************************* */

  // Close the suggestions when clicking outside of the component
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        autoCompleteRef.current &&
        !autoCompleteRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle search text changes with debounce
  useEffect(() => {
    let timeout = setTimeout(() => {
      if (searchText !== "") {
        const filteredResults = filterDataList(searchText);
        setSearchResults(filteredResults);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchText, dataList]);

  // Handle Tab key press
  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    if (value) {
      setSearchText(value);
    }
  }, [value]);

  return (
    <div className="position-relative" ref={autoCompleteRef}>
      <>
        <input
          type={type || "text"}
          name={fieldName}
          placeholder={placeholder}
          className={classNames}
          style={styles}
          required={required}
          onChange={(e) => {
            setShowSuggestions(true);
            setSearchText(e.target.value);
            handleChange(
              fieldName,
              is2digitFloat ? parseFloatData(e.target.value) : e.target.value
            );
          }}
          onKeyDown={handleKeyDown}
          value={value || ""}
        />
        <div
          // this button should be skipped when tabbing through inputs
          tabIndex="-1"
          aria-hidden="true"
          onClick={getAllResults}
          className="btn btn-light position-absolute end-0 top-0 h-100"
        >
          <i className="mdi mdi-chevron-down text-secondary"></i>
        </div>
      </>
      {showSuggestions && searchResults && (
        <div>
          {searchResults.length > 0 ? (
            <div
              className="d-flex flex-column bg-white suggestion-hover py-2 px-2 rounded-3 custom-shadow-sm"
              style={{
                position: "absolute",
                zIndex: 1000,
                width: suggestionsWidth || "100%",
                maxHeight: "300px",
                overflowY: "auto",
              }}
            >
              {searchResults.map((item, index) => (
                <div key={index}>
                  <button
                    type="button"
                    className="suggestion-hover btn btn-sm btn-light text-start w-100"
                    onClick={() => {
                      handleChange(fieldName, item[fieldName]);
                      setSearchText("");
                      setShowSuggestions(false);
                    }}
                  >
                    {item[resultsKey || fieldName]}
                  </button>
                  {index !== searchResults.length - 1 && (
                    <hr
                      style={{
                        margin: "0",
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div
              className="bg-light px-2 rounded-3 custom-shadow-sm"
              style={{
                position: "absolute",
                zIndex: 1000,
                width: "fit-content",
                maxHeight: "300px",
                overflowY: "auto",
              }}
            >
              <h5>No results found</h5>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
